import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  MenuToggle,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Spinner,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr, TreeRowWrapper } from '@patternfly/react-table';
import { SearchIcon } from '@patternfly/react-icons';
import { useBackupRuns } from '../../../hooks/useBackupRuns';
import { formatBytes } from '../../../utils/formatBytes';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import { renderRunVerificationLabels, renderSubjectStateLabels } from './backupStateLabels';
import {
  type BackupStatusFilter,
  filterBackups,
  flattenBackupTree,
  formatBackupAge,
  formatRunDuration,
} from './backupTree';
import type { BackupSummary } from '../../../types/models/Backup';

/**
 * Restore points fetched per subject on first expand. The endpoint's own maximum is 500; 200
 * comfortably covers the ~30 a subject holds under the current retention while bounding a
 * pathological one. Anything beyond that is reachable through the detail view.
 */
const CHILD_RUNS_LIMIT = 200;

const STATUS_LABELS: Record<BackupStatusFilter, string> = {
  all: 'All statuses',
  active: 'Active',
  retired: 'Retired',
  missing: 'Missing',
};

const STATUS_OPTIONS: BackupStatusFilter[] = ['all', 'active', 'retired', 'missing'];

export interface BackupTreeTableProps {
  /** The subjects to render, in the server's order — never re-sorted here. */
  subjects: BackupSummary[];
  /**
   * The threshold from `summary.stale_after_hours`, or `null` where the summary was not fetched.
   * `null` suppresses the "Stale" verdict entirely and shows only the factual age — which is why
   * the VM tab passes it, rather than hardcoding the number or firing an aggregate query per tab.
   */
  staleAfterHours: number | null;
  /** Accessible name for the table, e.g. "Backups list". */
  ariaLabel: string;
  /**
   * Search box, status filter and pagination. The page turns these on; the VM detail tab does
   * not — its list is a handful of rows. One component, two configurations.
   */
  withToolbar?: boolean;
  /** Rendered instead of the table when there are no subjects at all (before any filtering). */
  emptyState: React.ReactNode;
}

/**
 * The backups tree table, shared by the Backups page and the VM detail page's Snapshots tab.
 *
 * Parent rows are **subjects** (one backed-up directory); child rows are that subject's
 * **restore points**, fetched lazily on first expand and cached for the component's lifetime so
 * re-collapsing costs nothing. A child fetch that is slow or fails degrades into a single child
 * row — it never takes down the table.
 *
 * Everything here is read-only: the resource has no write routes, so there is no create, delete,
 * restore or "run backup now" affordance anywhere in this component, not even a disabled one.
 */
const BackupTreeTable: React.FunctionComponent<BackupTreeTableProps> = ({
  subjects,
  staleAfterHours,
  ariaLabel,
  withToolbar = false,
  emptyState,
}) => {
  const { runsById, loadingById, errorById, loadRuns } = useBackupRuns();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BackupStatusFilter>('all');
  const [isStatusSelectOpen, setIsStatusSelectOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set<number>());

  // Debounced so filtering does not run on every keystroke. `setPage(1)` sits inside the timeout
  // so it fires with the debounced value rather than once per character.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const filtersActive = debouncedSearch.trim() !== '' || statusFilter !== 'all';

  const filteredSubjects = useMemo(
    () => (withToolbar ? filterBackups(subjects, debouncedSearch, statusFilter) : subjects),
    [withToolbar, subjects, debouncedSearch, statusFilter],
  );

  const rows = useMemo(
    () =>
      flattenBackupTree(filteredSubjects, {
        isExpanded: (id) => expandedIds.has(id),
        runsFor: (id) => runsById[id],
        isLoadingRuns: (id) => loadingById[id] === true,
        runsError: (id) => errorById[id]?.message,
      }),
    [filteredSubjects, expandedIds, runsById, loadingById, errorById],
  );

  // Collapsed children are absent from `rows`, so this really is the visible row count — which is
  // what pagination must count. Expanding or collapsing a subject changes it.
  const itemCount = rows.length;

  const paginatedRows = useMemo(() => {
    if (!withToolbar) {
      return rows;
    }
    const start = (page - 1) * perPage;
    return rows.slice(start, start + perPage);
  }, [withToolbar, rows, page, perPage]);

  // Clamp rather than reset. A filter change resets to page 1 in the two effects above, but
  // expanding or collapsing a subject also changes the visible-row count — and resetting on that
  // would throw the reader back to page 1 every time they opened a row on page 2 or beyond,
  // making the tree's primary interaction unusable there. Clamping keeps the page in range,
  // which is what the reset was actually for.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(itemCount / perPage));
    setPage((current) => Math.min(current, lastPage));
  }, [itemCount, perPage]);

  const fetchRuns = useCallback(
    (subjectId: number) => {
      loadRuns(subjectId, { limit: CHILD_RUNS_LIMIT });
    },
    [loadRuns],
  );

  const toggleExpanded = useCallback(
    (subjectId: number) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(subjectId)) {
          next.delete(subjectId);
          return next;
        }
        next.add(subjectId);
        return next;
      });
      // Fetch once per subject; the cache survives a re-collapse, so re-opening is instant.
      if (!expandedIds.has(subjectId) && runsById[subjectId] === undefined && loadingById[subjectId] !== true) {
        fetchRuns(subjectId);
      }
    },
    [expandedIds, runsById, loadingById, fetchRuns],
  );

  const onClearFilters = useCallback(() => {
    setSearchValue('');
    setDebouncedSearch('');
    setStatusFilter('all');
  }, []);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  if (subjects.length === 0) {
    return <>{emptyState}</>;
  }

  const renderSubjectRow = (
    subject: BackupSummary,
    posInSet: number,
    childCount: number,
    expanded: boolean,
    rowIndex: number,
  ) => {
    // The SAME object goes to the TreeRowWrapper (which puts the ARIA attributes on the <tr>) and
    // to the first Td's `treeRow` (which draws the chevron and the indent). Passing it to only
    // one silently yields a tree with no indentation *or* no toggle.
    const treeRowProps = {
      isExpanded: expanded,
      isHidden: false,
      'aria-level': 1,
      'aria-posinset': posInSet,
      // The chevron renders only when this is > 0 — a subject with no restore points gets none.
      'aria-setsize': childCount,
      toggleAriaLabel: `Toggle restore points for ${subject.vm_name}`,
    };
    const badges = renderSubjectStateLabels(subject, staleAfterHours);
    const age = formatBackupAge(subject.hours_since_last_backup);

    return (
      // The highlight goes on the <tr> via className — on a <td> the tree indent insets it and
      // leaves a gap at the left edge.
      <TreeRowWrapper key={`subject-${subject.id}`} row={{ props: treeRowProps }} className="ancalagon-backup-vm-header">
        <Td
          dataLabel="Virtual machine"
          treeRow={{ onCollapse: () => toggleExpanded(subject.id), props: treeRowProps, rowIndex }}
        >
          <Flex
            spaceItems={{ default: 'spaceItemsSm' }}
            alignItems={{ default: 'alignItemsCenter' }}
            flexWrap={{ default: 'wrap' }}
          >
            <FlexItem>
              <Link to={`/datacenter/backups/${subject.id}`}>{subject.vm_name}</Link>
            </FlexItem>
            {badges.map((badge, i) => (
              <FlexItem key={i}>{badge}</FlexItem>
            ))}
          </Flex>
        </Td>
        <Td dataLabel="Hypervisor">{subject.hypervisor}</Td>
        <Td dataLabel="Backup target">{subject.backup_target}</Td>
        <Td dataLabel="Restore points">{subject.backup_count}</Td>
        <Td dataLabel="Size">
          <Tooltip
            content={`${formatBytes(subject.allocated_bytes)} allocated on the backup filesystem. Sparse images allocate less than their apparent size; block rounding can make it marginally more.`}
          >
            <span>{formatBytes(subject.size_bytes)}</span>
          </Tooltip>
        </Td>
        <Td dataLabel="Last backup">
          {formatWallClockDateTime(subject.last_backup_at)}
          {age !== null && (
            <>
              {' '}
              <Content component="small">({age})</Content>
            </>
          )}
        </Td>
      </TreeRowWrapper>
    );
  };

  /** Shared level-2 props: leaves must pass `aria-setsize: 0` or they get a dead chevron. */
  const leafProps = (posInSet: number) => ({
    isExpanded: false,
    isHidden: false,
    'aria-level': 2,
    'aria-posinset': posInSet,
    'aria-setsize': 0,
  });

  return (
    <>
      {withToolbar && (
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by VM name..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem>
              <Select
                isOpen={isStatusSelectOpen}
                selected={statusFilter}
                onSelect={(_event, value) => {
                  setStatusFilter(value as BackupStatusFilter);
                  setIsStatusSelectOpen(false);
                }}
                onOpenChange={(isOpen) => setIsStatusSelectOpen(isOpen)}
                toggle={(toggleRef) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsStatusSelectOpen(!isStatusSelectOpen)}
                    isExpanded={isStatusSelectOpen}
                    aria-label="Filter by backup status"
                  >
                    {STATUS_LABELS[statusFilter]}
                  </MenuToggle>
                )}
              >
                <SelectList>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectOption key={option} value={option}>
                      {STATUS_LABELS[option]}
                    </SelectOption>
                  ))}
                </SelectList>
              </Select>
            </ToolbarItem>
            <ToolbarItem>
              <Button variant="link" isInline isDisabled={!filtersActive} onClick={onClearFilters}>
                Clear filters
              </Button>
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={itemCount}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
      )}

      {withToolbar && itemCount === 0 ? (
        <EmptyState titleText="No backups match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Try a different VM name or status, or clear the filters to see everything.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={onClearFilters}>
                Clear filters
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : (
        <Table isTreeTable aria-label={ariaLabel} variant="compact">
          <Thead>
            <Tr>
              <Th>Virtual machine</Th>
              <Th>Hypervisor</Th>
              <Th>Backup target</Th>
              <Th>Restore points</Th>
              <Th>Size</Th>
              <Th>Last backup</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedRows.map((row, idx) => {
              if (row.kind === 'subject') {
                return renderSubjectRow(row.subject, row.posInSet, row.childCount, row.expanded, idx);
              }

              if (row.kind === 'run') {
                const props = leafProps(row.posInSet);
                // The first cell is a badge LIST that currently renders nothing. A future
                // "tested / verified" label belongs to an individual restore point, so it drops
                // in here without restructuring the table or adding a column. No such field
                // exists on the backend today — nothing is invented for it.
                const verificationBadges = renderRunVerificationLabels(row.run);
                return (
                  <TreeRowWrapper key={row.key} row={{ props }}>
                    <Td
                      dataLabel="Restore point"
                      treeRow={{ onCollapse: () => undefined, props, rowIndex: idx }}
                    >
                      <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                        <FlexItem>{formatWallClockDateTime(row.run.run_at)}</FlexItem>
                        {verificationBadges.map((badge, i) => (
                          <FlexItem key={i}>{badge}</FlexItem>
                        ))}
                      </Flex>
                    </Td>
                    {/* The null triple (started_at / ended_at / duration_seconds) renders as an
                        em dash — a duration is never inferred from run_at. */}
                    <Td dataLabel="Duration">{formatRunDuration(row.run.duration_seconds)}</Td>
                    <Td dataLabel="File">
                      <Content component="small">{row.run.file}</Content>
                    </Td>
                    <Td />
                    <Td dataLabel="Metadata size">
                      <Tooltip content="Size of this restore point's libvirt XML. The disk data lives in the qcow2 chains, not here.">
                        <span>{formatBytes(row.run.size_bytes)}</span>
                      </Tooltip>
                    </Td>
                    <Td />
                  </TreeRowWrapper>
                );
              }

              const props = leafProps(row.posInSet);
              return (
                <TreeRowWrapper key={row.key} row={{ props }}>
                  <Td treeRow={{ onCollapse: () => undefined, props, rowIndex: idx }} colSpan={6}>
                    {row.kind === 'runs-loading' && (
                      <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                        <FlexItem>
                          <Spinner size="sm" aria-label="Loading restore points" />
                        </FlexItem>
                        <FlexItem>
                          <Content component="small">Loading restore points…</Content>
                        </FlexItem>
                      </Flex>
                    )}
                    {row.kind === 'runs-error' && (
                      <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                        <FlexItem>
                          <Content component="small">Could not load restore points: {row.message}</Content>
                        </FlexItem>
                        <FlexItem>
                          <Button variant="link" isInline onClick={() => fetchRuns(row.subjectId)}>
                            Retry
                          </Button>
                        </FlexItem>
                      </Flex>
                    )}
                    {row.kind === 'runs-more' && (
                      <Content component="small">
                        <Link to={`/datacenter/backups/${row.subjectId}`}>
                          {row.remaining} more restore {row.remaining === 1 ? 'point' : 'points'} — view all
                        </Link>
                      </Content>
                    )}
                  </Td>
                </TreeRowWrapper>
              );
            })}
          </Tbody>
        </Table>
      )}

      {withToolbar && itemCount > 0 && (
        <Pagination
          itemCount={itemCount}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      )}
    </>
  );
};

export default BackupTreeTable;
